import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { User, Trophy, Phone, FileText, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { 
  completeProfile, 
  getProfileData, 
  getAvailableGames, 
  ProfileCompletionData 
} from "@/services/profileCompletionService";

interface CompleteProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  onComplete: () => void;
}

const CompleteProfileModal: React.FC<CompleteProfileModalProps> = ({
  isOpen,
  onClose,
  userId,
  onComplete
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<ProfileCompletionData>({
    nickname: '',
    preferredGame: '',
    role: 'jogador',
    whatsapp_number: '',
    bio: ''
  });

  const games = getAvailableGames();

  // Carregar dados atuais do perfil
  useEffect(() => {
    if (isOpen && userId) {
      loadProfileData();
    }
  }, [isOpen, userId]);

  const loadProfileData = async () => {
    try {
      const profile = await getProfileData(userId);
      if (profile) {
        setFormData({
          nickname: profile.nickname || '',
          preferredGame: profile.preferred_game || '',
          role: (profile.role as 'mentor' | 'jogador') || 'jogador',
          whatsapp_number: profile.whatsapp_number || '',
          bio: profile.bio || ''
        });
      }
    } catch (error) {
      console.error('Erro ao carregar dados do perfil:', error);
    }
  };

  const handleSubmit = async () => {
    if (!formData.nickname || !formData.preferredGame || !formData.role) {
      toast({
        title: "Campos obrigatórios",
        description: "Por favor, preencha todos os campos obrigatórios",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    
    try {
      const result = await completeProfile(userId, formData);
      
      if (result.success) {
        toast({
          title: "Perfil completado!",
          description: "Seu perfil foi atualizado com sucesso",
          variant: "default"
        });
        onComplete();
        onClose();
      } else {
        toast({
          title: "Erro ao completar perfil",
          description: result.error || "Erro desconhecido",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Erro ao completar perfil:', error);
      toast({
        title: "Erro",
        description: "Erro interno. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (step === 1) {
      if (!formData.nickname || !formData.preferredGame) {
        toast({
          title: "Campos obrigatórios",
          description: "Por favor, preencha o nome e jogo preferido",
          variant: "destructive"
        });
        return;
      }
    }
    setStep(step + 1);
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-[500px] bg-gray-900 border-gray-800">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            Complete seu perfil
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Para garantir a melhor experiência, precisamos de algumas informações adicionais.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Progress bar */}
          <div className="w-full bg-gray-800 rounded-full h-2">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(step / 2) * 100}%` }}
            />
          </div>

          {step === 1 && (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-white mb-2">
                  Informações básicas
                </h3>
                <p className="text-gray-400 text-sm">
                  Como você gostaria de ser chamado e qual seu jogo preferido?
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="nickname" className="text-white flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Nome de usuário *
                  </Label>
                  <Input
                    id="nickname"
                    value={formData.nickname}
                    onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                    placeholder="Como você gostaria de ser chamado?"
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>

                <div>
                  <Label htmlFor="preferredGame" className="text-white flex items-center gap-2">
                    <Trophy className="h-4 w-4" />
                    Jogo preferido *
                  </Label>
                  <Select 
                    value={formData.preferredGame} 
                    onValueChange={(value) => setFormData({ ...formData, preferredGame: value })}
                  >
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                      <SelectValue placeholder="Selecione seu jogo preferido" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      {games.map((game) => (
                        <SelectItem key={game.value} value={game.value} className="text-white">
                          {game.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="whatsapp" className="text-white flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    WhatsApp (opcional)
                  </Label>
                  <Input
                    id="whatsapp"
                    value={formData.whatsapp_number}
                    onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
                    placeholder="(11) 99999-9999"
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-white mb-2">
                  Escolha seu perfil
                </h3>
                <p className="text-gray-400 text-sm">
                  Você deseja ofertar mentorias ou receber coaching?
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <Card 
                  className={`cursor-pointer transition-all duration-200 ${
                    formData.role === 'jogador' 
                      ? 'bg-blue-900/30 border-blue-500' 
                      : 'bg-gray-800 border-gray-700 hover:bg-gray-700'
                  }`}
                  onClick={() => setFormData({ ...formData, role: 'jogador' })}
                >
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white text-lg">
                      🎮 Jogador
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                      Quero receber mentorias e melhorar minhas habilidades
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>• Agendar mentorias com experts</li>
                      <li>• Comprar conteúdos exclusivos</li>
                      <li>• Participar de comunidades</li>
                    </ul>
                  </CardContent>
                </Card>

                <Card 
                  className={`cursor-pointer transition-all duration-200 ${
                    formData.role === 'mentor' 
                      ? 'bg-green-900/30 border-green-500' 
                      : 'bg-gray-800 border-gray-700 hover:bg-gray-700'
                  }`}
                  onClick={() => setFormData({ ...formData, role: 'mentor' })}
                >
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white text-lg">
                      🎓 Mentor
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                      Quero ofertar mentorias e compartilhar conhecimento
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li>• Criar mentorias e conteúdos</li>
                      <li>• Gerenciar comunidades</li>
                      <li>• Monetizar conhecimento</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>

              <div>
                <Label htmlFor="bio" className="text-white flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Biografia (opcional)
                </Label>
                <Textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder="Conte um pouco sobre você e sua experiência..."
                  className="bg-gray-800 border-gray-700 text-white"
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={prevStep}
              disabled={step === 1}
              className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
            >
              Anterior
            </Button>

            {step < 2 ? (
              <Button
                onClick={nextStep}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Próximo
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Completar perfil
              </Button>
            )}
          </div>

          <div className="flex items-center justify-center pt-4 border-t border-gray-800">
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <AlertCircle className="h-4 w-4" />
              Você poderá alterar essas informações a qualquer momento
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CompleteProfileModal; 